import storage from "../store";

type ApiMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface RequestOptions {
  signal?: AbortSignal;
  headers?: Record<string, string>;
  useAuth?: boolean;
  onProgress?: (event: ProgressEvent<EventTarget>) => void;
}

interface RequestConfig extends RequestOptions {
  method?: ApiMethod;
  data?: any;
}

export const getUrlParam = (key: string): string | null => {
  const params = new URLSearchParams(window.location.search);
  return params.get(key);
};

export const DEFAULT_API_BASE_URL = "https://zoop-api.thewatchtower.ae";

export const resolveApiBaseUrl = (value?: string) => {
  const normalizedValue = value?.trim();

  if (
    !normalizedValue ||
    ["undefined", "null", ""].includes(normalizedValue.toLowerCase())
  ) {
    return DEFAULT_API_BASE_URL;
  }

  const normalizedUrl = normalizedValue.replace(/\/+$/, "");
  const appHosts = [
    "https://zoop.thewatchtower.ae",
    "http://zoop.thewatchtower.ae",
  ];

  if (appHosts.includes(normalizedUrl)) {
    return DEFAULT_API_BASE_URL;
  }

  return normalizedUrl;
};

class ApiClient {
  private baseUrl: string;
  private token?: string;

  constructor(baseUrl: string) {
    this.baseUrl = resolveApiBaseUrl(baseUrl);
    if (storage.get("_app_token")) {
      this.token = storage.get("_app_token") || "";
    }
  }
  getBaseURL() {
    return this.baseUrl;
  }
  setToken(token: string) {
    this.token = token;
    storage.set("_app_token", token);
  }

  clearToken() {
    this.token = undefined;
    storage.set("_app_token", "");
  }

  getToken() {
    return this?.token || storage.get("_app_token");
  }

  private getHeaders(
    customHeaders: Record<string, string> = {},
    isFormData = false,
    useAuth = true,
  ) {
    return {
      ...(isFormData
        ? {}
        : {
            "Content-Type": "application/json",
          }),

      ...(useAuth &&
        this.token && {
          Authorization: `Bearer ${this.token}`,
        }),

      ...customHeaders,
    };
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get("content-type");

    let result: any;

    try {
      if (contentType?.includes("application/json")) {
        result = await response.json();
      } else {
        result = await response.text();
      }
    } catch {
      result = null;
    }

    if (!response.ok) {
      throw (
        result || {
          status: false,
          message: response.statusText || "Request failed",
        }
      );
    }

    return result;
  }

  private buildRequestUrl(endpoint: string) {
    const normalizedEndpoint = endpoint.startsWith("/")
      ? endpoint
      : `/${endpoint}`;
    return `${this.baseUrl}${normalizedEndpoint}`;
  }

  private uploadRequest<T>(
    endpoint: string,
    method: ApiMethod,
    data: FormData,
    options: RequestOptions = {},
  ): Promise<T> {
    const { signal, headers = {}, useAuth = true, onProgress } = options;

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      signal?.addEventListener("abort", () => {
        xhr.abort();

        reject({
          status: false,
          message: "Request cancelled",
        });
      });

      if (onProgress) {
        xhr.upload.onprogress = onProgress;
      }

      xhr.onreadystatechange = () => {
        if (xhr.readyState !== 4) {
          return;
        }

        let result: any;

        try {
          result = JSON.parse(xhr.responseText);
        } catch {
          result = xhr.responseText;
        }

        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(result);
        } else {
          reject(
            result || {
              status: false,
              message: "Request failed",
            },
          );
        }
      };

      xhr.onerror = () => {
        reject({
          status: false,
          message: "Network error",
        });
      };

      xhr.open(method, this.buildRequestUrl(endpoint));

      const requestHeaders = this.getHeaders(headers, true, useAuth);

      Object.entries(requestHeaders).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });

      xhr.send(data);
    });
  }

  async request<T>(
    endpoint: string,
    {
      method = "GET",
      data,
      headers = {},
      signal,
      useAuth = true,
      onProgress,
    }: RequestConfig = {},
  ): Promise<T> {
    const isFormData = data instanceof FormData;

    try {
      if (isFormData && onProgress) {
        return this.uploadRequest<T>(endpoint, method, data, {
          signal,
          headers,
          useAuth,
          onProgress,
        });
      }

      const response = await fetch(this.buildRequestUrl(endpoint), {
        method,
        signal,
        headers: this.getHeaders(headers, isFormData, useAuth),
        body:
          method === "GET"
            ? undefined
            : isFormData
              ? data
              : JSON.stringify(data),
      });

      return this.handleResponse<T>(response);
    } catch (error: any) {
      if (error?.name === "AbortError") {
        throw {
          status: false,
          message: "Request cancelled",
        };
      }

      throw {
        status: false,
        message: error?.message || "Something went wrong",
        ...error,
      };
    }
  }

  get<T>(endpoint: string, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      method: "GET",
      ...options,
    });
  }

  post<T>(endpoint: string, data?: any, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      method: "POST",
      data,
      ...options,
    });
  }

  put<T>(endpoint: string, data?: any, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      method: "PUT",
      data,
      ...options,
    });
  }

  patch<T>(endpoint: string, data?: any, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      method: "PATCH",
      data,
      ...options,
    });
  }

  delete<T>(endpoint: string, data?: any, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      method: "DELETE",
      data,
      ...options,
    });
  }
}

const API_URL = import.meta.env.VITE_API_URL;

export const API = new ApiClient(API_URL);

export function resolveAssetUrl(path?: string | null): string | null {
  if (!path) return null;
  return /^https?:\/\//i.test(path) ? path : `${API.getBaseURL()}${path}`;
}

export function formatDate(dateString: string) {
  const date = new Date(dateString.replace(" ", "T"));

  if (isNaN(date.getTime())) {
    return "Invalid date";
  }

  const day = date.getDate();
  const month = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";

  hours = hours % 12 || 12;

  return `${day} ${month} ${year} ${hours}:${minutes} ${ampm}`;
}


/**
 * Converts a data URL to a File object.
 * @param dataUrl - The data URL string (e.g. "data:image/jpeg;base64,...")
 * @param filename - The desired file name (e.g. "image.jpg")
 * @returns A File instance ready for FormData.
 */
export function dataURLtoFile(dataUrl: string, filename: string): File {
  // Split and extract MIME type
  const [header, base64] = dataUrl.split(',');
  const mimeMatch = header.match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';

  // Decode base64 to binary
  const binary = atob(base64);
  const len = binary.length;
  const u8arr = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    u8arr[i] = binary.charCodeAt(i);
  }

  // Create Blob and then File
  const blob = new Blob([u8arr], { type: mime });
  return new File([blob], filename, { type: mime });
}

/**
 * Downloads an image from a URL and returns it as a File.
 * Returns null if the image cannot be fetched.
 */
export async function imageUrlToFile(
  url: string,
  fileName?: string,
): Promise<File | null> {
  const response = await fetch(url);

  console.log(response);

  if (!response.ok) {
    console.log("Not OK:", response.status, response.statusText);
    return null;
  }

  const blob = await response.blob();

  return new File(
    [blob],
    fileName ?? url.split("/").pop() ?? "image",
    { type: blob.type }
  );
}


